<script>
export default {
  name: 'RecentlyPlayedGames',
  props: {
    games: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      canScrollBack: false,
      canScrollForward: false
    }
  },
  watch: {
    games () {
      this.$nextTick(this.updateScrollState)
    }
  },
  mounted () {
    this.$nextTick(this.updateScrollState)
  },
  methods: {
    coverImage (game) {
      if (!game || !game.image) return ''
      if (typeof game.image === 'string') return game.image
      return game.image.vertical || game.image.horizontal || ''
    },
    gameRoute (game) {
      return {
        name: 'PlayGame',
        query: {
          id: game.id,
          provider: game.provider,
          gameCategory: game.gameCategory
        }
      }
    },
    scrollGames (direction) {
      const scroller = this.$refs.gameScroller
      if (!scroller) return

      const distance = Math.max(280, scroller.clientWidth * 0.72)
      scroller.scrollBy({
        left: direction * distance,
        behavior: 'smooth'
      })
    },
    updateScrollState () {
      const scroller = this.$refs.gameScroller
      if (!scroller) return

      const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth)
      this.canScrollBack = scroller.scrollLeft > 4
      this.canScrollForward = scroller.scrollLeft < maxScrollLeft - 4
    }
  }
}
</script>

<template>
  <section
    v-if="games.length"
    id="recently-played-games"
    class="recent-games animated fadeInUp"
    data-animatable="fadeInUp"
    data-delay="120"
    :aria-label="$t('recentGames.title')"
  >
    <div class="recent-games__heading">
      <div class="recent-games__title-group">
        <span class="recent-games__heart" aria-hidden="true">♥</span>
        <div class="recent-games__copy">
          <h2 class="recent-games__title">
            {{ $t('recentGames.title') }}
            <span class="recent-games__badge">{{ $t('recentGames.badge') }}</span>
          </h2>
          <p class="recent-games__subtitle">{{ $t('recentGames.subtitle') }}</p>
        </div>
      </div>

      <div class="recent-games__controls">
        <button
          type="button"
          class="recent-games__control"
          :disabled="!canScrollBack"
          :aria-label="$t('recentGames.previous')"
          @click="scrollGames(-1)"
        >
          ‹
        </button>
        <button
          type="button"
          class="recent-games__control"
          :disabled="!canScrollForward"
          :aria-label="$t('recentGames.next')"
          @click="scrollGames(1)"
        >
          ›
        </button>
      </div>
    </div>

    <div
      ref="gameScroller"
      class="recent-games__scroller"
      @scroll.passive="updateScrollState"
    >
      <router-link
        v-for="(game, index) in games"
        :key="`${game.provider}:${game.id}`"
        class="recent-game-card"
        :to="gameRoute(game)"
        target="_blank"
        rel="noopener"
      >
        <span class="recent-game-card__art">
          <img
            v-if="coverImage(game)"
            class="recent-game-card__image"
            :src="coverImage(game)"
            :alt="game.gameName || game.id"
            loading="lazy"
          >
          <span v-else class="recent-game-card__fallback" aria-hidden="true">♠</span>
          <span class="recent-game-card__rank">#{{ index + 1 }}</span>
          <span class="recent-game-card__play" aria-hidden="true">
            <i class="fas fa-play"></i>
          </span>
        </span>
        <span class="recent-game-card__meta">
          <strong class="recent-game-card__name">{{ game.gameName || game.id }}</strong>
          <span class="recent-game-card__detail">
            {{ game.provider }} · {{ $t('recentGames.playCount', { count: game.recentPlayCount || 1 }) }}
          </span>
        </span>
      </router-link>
    </div>
  </section>
</template>

<style scoped>
.recent-games {
  position: relative;
  margin: 4px 10px 28px;
  padding: 20px;
  overflow: hidden;
  color: #fff;
  background:
    linear-gradient(110deg, rgba(255, 79, 184, 0.13), transparent 30%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.04), transparent),
    #1b0c2b;
  border: 1px solid rgba(255, 131, 208, 0.27);
  border-radius: 20px;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.24);
  isolation: isolate;
}

.recent-games::before {
  position: absolute;
  top: -100px;
  left: -76px;
  z-index: -1;
  width: 220px;
  height: 220px;
  background: rgba(255, 79, 184, 0.16);
  border-radius: 50%;
  content: '';
}

.recent-games__heading {
  display: flex;
  gap: 14px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
}

.recent-games__title-group {
  display: flex;
  min-width: 0;
  gap: 11px;
  align-items: center;
}

.recent-games__heart {
  display: grid;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  place-items: center;
  color: #fff;
  background: linear-gradient(145deg, #ff65bf, #d91f87);
  border-radius: 13px;
  box-shadow: 0 8px 24px rgba(255, 53, 167, 0.34), inset 0 0 0 1px rgba(255, 255, 255, 0.28);
  font-size: 20px;
}

.recent-games__copy {
  min-width: 0;
}

.recent-games__title {
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 0;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.1;
}

.recent-games__badge {
  padding: 3px 8px;
  color: #ffd9ef;
  background: rgba(255, 79, 184, 0.16);
  border: 1px solid rgba(255, 113, 197, 0.23);
  border-radius: 999px;
  font-size: 10px;
  font-weight: 400;
  white-space: nowrap;
}

.recent-games__subtitle {
  margin: 3px 0 0;
  overflow: hidden;
  color: #aa9db5;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recent-games__controls {
  display: flex;
  flex: 0 0 auto;
  gap: 6px;
}

.recent-games__control {
  display: grid;
  width: 35px;
  height: 35px;
  padding: 0;
  place-items: center;
  color: #fff;
  background: #2c183f;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 50%;
  cursor: pointer;
  font-size: 21px;
  line-height: 1;
  transition: color 0.2s ease, background 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
}

.recent-games__control:hover:not(:disabled) {
  color: #2d1738;
  background: #ff7bd0;
  transform: translateY(-1px);
}

.recent-games__control:disabled {
  cursor: default;
  opacity: 0.32;
}

.recent-games__scroller {
  display: grid;
  grid-auto-columns: minmax(135px, 160px);
  grid-auto-flow: column;
  gap: 12px;
  overflow-x: auto;
  overscroll-behavior-inline: contain;
  scrollbar-width: none;
  scroll-snap-type: inline mandatory;
}

.recent-games__scroller::-webkit-scrollbar {
  display: none;
}

.recent-game-card {
  min-width: 0;
  overflow: hidden;
  color: #fff;
  background: #29193a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  scroll-snap-align: start;
  text-decoration: none;
  transition: border-color 0.22s ease, box-shadow 0.22s ease, transform 0.22s ease;
}

.recent-game-card:hover,
.recent-game-card:focus-visible {
  z-index: 2;
  color: #fff;
  border-color: rgba(255, 111, 196, 0.67);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.34), 0 0 0 1px rgba(255, 79, 184, 0.18);
  outline: none;
  text-decoration: none;
  transform: translateY(-4px);
}

.recent-game-card__art {
  position: relative;
  display: grid;
  aspect-ratio: 1 / 1;
  place-items: center;
  overflow: hidden;
  background: linear-gradient(145deg, #6e2a92, #1c5781);
}

.recent-game-card__art::after {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 52%, rgba(12, 3, 20, 0.64));
  content: '';
  pointer-events: none;
}

.recent-game-card__image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.28s ease;
}

.recent-game-card:hover .recent-game-card__image,
.recent-game-card:focus-visible .recent-game-card__image {
  transform: scale(1.07);
}

.recent-game-card__fallback {
  color: rgba(255, 255, 255, 0.83);
  font-size: 54px;
  filter: drop-shadow(0 7px 9px rgba(0, 0, 0, 0.35));
}

.recent-game-card__rank {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  padding: 4px 7px;
  color: #382400;
  background: rgba(255, 207, 105, 0.97);
  border-radius: 7px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  font-size: 9px;
  font-weight: 600;
}

.recent-game-card__play {
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 2;
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  color: #fff;
  background: rgba(17, 5, 28, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.32);
  border-radius: 50%;
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.recent-game-card__play .fas {
  margin-left: 2px;
  font-size: 10px;
}

.recent-game-card:hover .recent-game-card__play,
.recent-game-card:focus-visible .recent-game-card__play {
  opacity: 1;
  transform: translateY(0);
}

.recent-game-card__meta {
  display: block;
  padding: 9px 10px 10px;
}

.recent-game-card__name,
.recent-game-card__detail {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recent-game-card__name {
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}

.recent-game-card__detail {
  margin-top: 2px;
  color: #aa9db5;
  font-size: 9px;
}

@media (max-width: 767.98px) {
  .recent-games {
    margin: 2px 0 20px;
    padding: 14px 12px;
    border-radius: 16px;
  }

  .recent-games__heading {
    margin-bottom: 11px;
  }

  .recent-games__title-group {
    gap: 8px;
  }

  .recent-games__heart {
    flex-basis: 34px;
    width: 34px;
    height: 34px;
    border-radius: 11px;
    font-size: 16px;
  }

  .recent-games__title {
    gap: 5px;
    font-size: 14px;
  }

  .recent-games__badge {
    padding: 2px 6px;
    font-size: 8px;
  }

  .recent-games__subtitle {
    max-width: calc(100vw - 110px);
    font-size: 9px;
  }

  .recent-games__controls {
    display: none;
  }

  .recent-games__scroller {
    grid-auto-columns: 108px;
    gap: 9px;
  }

  .recent-game-card {
    border-radius: 12px;
  }

  .recent-game-card__rank {
    top: 6px;
    left: 6px;
    padding: 3px 5px;
    font-size: 7px;
  }

  .recent-game-card__play {
    right: 6px;
    bottom: 6px;
    width: 28px;
    height: 28px;
    opacity: 1;
    transform: none;
  }

  .recent-game-card__meta {
    padding: 7px 8px 8px;
  }

  .recent-game-card__name {
    font-size: 9px;
  }

  .recent-game-card__detail {
    font-size: 7px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .recent-game-card,
  .recent-game-card__image,
  .recent-game-card__play,
  .recent-games__control {
    transition: none;
  }
}
</style>
